import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { Download } from "lucide-react";
import React, { useEffect } from "react";
import { BsFilePdf } from "react-icons/bs";

const PersonalInfoVariations = ({ data }) => {
  const names = data?.personal_info_variation?.name_variations;
  const addresses = data?.personal_info_variation?.address_variations;
  const dobs = data?.personal_info_variation?.date_of_birth_variations;

  // Find the longest array to ensure we render enough rows for all data
  const maxRows = Math.max(names.length, addresses.length, dobs.length);

  // useEffect(() => {
  //   console.log("data", data);
  // }, [data]);

  const handlePdfExportAll = () => {
    try {
      if (!data) return;

      const doc = new jsPDF("p", "mm", "a4");

      const customer = data.customer_identity;

      // Name
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.setTextColor(0, 102, 204); // Blue
      doc.text(customer.name || "-", 14, 15);

      // Score
      doc.setFontSize(14);
      doc.setTextColor(0, 128, 0); // Green
      doc.text(`Score: ${customer.score_value || "-"}`, 160, 15);
      doc.setTextColor(0, 0, 0);

      // Customer Info
      doc.setFont(undefined, "normal");
      doc.setFontSize(10);

      let y = 28;

      const details = [
        ["DOB", customer.dob],
        ["PAN", customer.pan],
        ["Email", customer.email],
        ["Phone", customer.phone],
        ["Address", customer.address],
      ];

      details.forEach(([label, value]) => {
        doc.text(`${label}: ${value || "-"}`, 14, y);
        y += label === "Address" ? 10 : 7;
      });

      y += 5;

      // Main Heading
      doc.setFont(undefined, "bold");
      doc.setFontSize(14);
      doc.text("PERSONAL INFO VARIATIONS", 14, y);

      y += 10;

      // -------------------------
      // Name Variations
      // -------------------------
      doc.setFontSize(12);
      doc.text("Name Variations", 14, y);

      autoTable(doc, {
        startY: y + 3,
        head: [["Value", "Reported Date"]],
        body:
          data.personal_info_variation.name_variations?.map((item) => [
            item.value,
            item.reported_date,
          ]) || [],
        theme: "grid",
        headStyles: {
          fillColor: [41, 128, 185],
        },
      });

      y = doc.lastAutoTable.finalY + 10;

      // -------------------------
      // Address Variations
      // -------------------------
      doc.setFontSize(12);
      doc.text("Address Variations", 14, y);

      autoTable(doc, {
        startY: y + 3,
        head: [["Value", "Reported Date"]],
        body:
          data.personal_info_variation.address_variations?.map((item) => [
            item.value,
            item.reported_date,
          ]) || [],
        theme: "grid",
        headStyles: {
          fillColor: [41, 128, 185],
        },
        columnStyles: {
          0: { cellWidth: 140 },
          1: { cellWidth: 40 },
        },
        styles: {
          overflow: "linebreak",
        },
      });

      y = doc.lastAutoTable.finalY + 10;

      // -------------------------
      // DOB Variations
      // -------------------------
      doc.setFontSize(12);
      doc.text("DOB Variations", 14, y);

      autoTable(doc, {
        startY: y + 3,
        head: [["Value", "Reported Date"]],
        body:
          data.personal_info_variation.date_of_birth_variations?.map((item) => [
            item.value,
            item.reported_date,
          ]) || [],
        theme: "grid",
        headStyles: {
          fillColor: [41, 128, 185],
        },
      });

      doc.save("crif_score_report.pdf");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex justify-center font-sans text-gray-800">
      <div className="w-full max-w-7xl bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            Personal Information Variations
          </h2>
          <button
            onClick={handlePdfExportAll}
            className="group relative inline-flex items-center gap-2 px-3 py-2 font-semibold bg-red-50 border border-red-400 text-red-800 hover:border-none hover:bg-red-700 hover:text-white rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-95 cursor-pointer"
          >
            <BsFilePdf className="w-5 h-5 relative z-10 transition-transform duration-300 " />
            <span className="relative z-10 tracking-wide text-xs">
              Download
            </span>
          </button>{" "}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              {/* Top Level Category Headers */}
              <tr className="bg-blue-50 border-b border-gray-300">
                <th
                  colSpan="2"
                  className="px-4 py-3 text-sm font-bold text-blue-900 border-r border-gray-300 text-center uppercase tracking-wide"
                >
                  Name Variations
                </th>
                <th
                  colSpan="2"
                  className="px-4 py-3 text-sm font-bold text-blue-900 border-r border-gray-300 text-center uppercase tracking-wide"
                >
                  Address Variations
                </th>
                <th
                  colSpan="2"
                  className="px-4 py-3 text-sm font-bold text-blue-900 text-center uppercase tracking-wide"
                >
                  DOB Variations
                </th>
              </tr>
              {/* Secondary Sub-headers */}
              <tr className="bg-gray-100 border-b border-gray-300">
                <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase w-[15%]">
                  Value
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase border-r border-gray-300 w-[10%]">
                  Date
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase w-[40%]">
                  Value
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase border-r border-gray-300 w-[10%]">
                  Date
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase w-[15%]">
                  Value
                </th>
                <th className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase w-[10%]">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Array.from({ length: maxRows }).map((_, index) => (
                <tr
                  key={index}
                  className="hover:bg-blue-50/50 transition-colors duration-150 ease-in-out"
                >
                  {/* Name Columns */}
                  <td className="px-4 py-3 text-sm text-gray-800 align-top">
                    {names[index]?.value || (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-200 align-top whitespace-nowrap">
                    {names[index]?.reported_date || (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>

                  {/* Address Columns */}
                  <td className="px-4 py-3 text-sm text-gray-800 align-top">
                    {addresses[index]?.value || (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-200 align-top whitespace-nowrap">
                    {addresses[index]?.reported_date || (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>

                  {/* DOB Columns */}
                  <td className="px-4 py-3 text-sm text-gray-800 align-top">
                    {dobs[index]?.value || (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 align-top whitespace-nowrap">
                    {dobs[index]?.reported_date || (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoVariations;
